import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { DateTime } from 'luxon';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import interactionPlugin, {
  EventResizeDoneArg,
} from '@fullcalendar/interaction';
import { useAuth } from '../../../../contexts/AuthContext';
import { NylasCalendarEventDataFragment } from '../../../../generated/graphql';
import { useCalendarEvents } from './hooks/useCalendarEvents';
import { CalendarHeader } from './components/CalendarHeader';
import EventDetailsPopover from './components/EventDetailsPopover';
import CreateEventModal from './components/CreateEventModal';
import EditEventModal from './components/EditEventModal';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import './CalendarV2.styles.css';
import { EventDropArg } from '@fullcalendar/core';
import { useEventMutation } from './hooks/useEventMutations';
import { useCalendars } from './hooks/useCalendars';
import { useResources } from './hooks/useResources';

import { logger } from '../../../../lib/logger';
import { useAutoRefresh } from './hooks/useAutoRefresh';
import { useCalendarViewState } from './hooks/useCalendarViewState';
import { useCalendarViewStore } from './stores/useCalendarViewStore';
import {
  CalendarViewType,
  ViewMode,
  shouldRestorePersistedDate,
} from './utils/enums';
import { transformEventForCalendar } from './utils/eventTransformers';
import { resolveCalendarAnchorDate } from './utils/dateTimeUtils';
import luxonPlugin from '@fullcalendar/luxon2';

const DEFAULT_EVENT_TITLE = 'Busy';

const SmallEventContent = ({ event }: { event: any }) => (
  <div className="cursor-pointer overflow-hidden p-0 transition-all hover:bg-opacity-90">
    <div className="truncate whitespace-nowrap text-small-caption font-medium">
      {event.title || DEFAULT_EVENT_TITLE}
    </div>
  </div>
);

const MonthEventContent = ({ event }: { event: any }) => {
  const hexColor = event.backgroundColor || '#4CAF50';
  const isMultiDay =
    event.allDay ||
    (event.start &&
      event.end &&
      new Date(event.start).toDateString() !==
        new Date(event.end).toDateString());

  return (
    <div
      className="cursor-pointer overflow-hidden rounded px-1 py-0.5 transition-all hover:opacity-90"
      style={{
        backgroundColor: `${hexColor}15`,
        borderLeft: `3px solid ${hexColor}`,
      }}
    >
      <div
        className="truncate whitespace-nowrap text-small-caption font-medium"
        style={{
          ...(!isMultiDay && { color: `${hexColor}` }),
        }}
      >
        {event.title || DEFAULT_EVENT_TITLE}
      </div>
    </div>
  );
};

const RegularEventContent = ({
  event,
  timeText,
}: {
  event: any;
  timeText: string;
}) => (
  <div className="cursor-pointer p-0.5 transition-all hover:bg-opacity-90">
    <div className="truncate text-small-caption font-medium">
      {event.title || DEFAULT_EVENT_TITLE}
    </div>
    <div className="truncate text-small-caption opacity-80">{timeText}</div>
  </div>
);

const Calendar = () => {
  const { authedProviderUser } = useAuth();
  const calendarRef = useRef(null);
  const isNylasConnected = authedProviderUser?.hasNylasIntegration;

  // Fetch available calendars and resources
  const {
    calendars,
    loading: isLoadingCalendars,
    getCalendarByEmail,
  } = useCalendars();
  const { resources, loading: isLoadingResources } = useResources();

  // View state management
  const {
    viewType,
    viewMode,
    selectedCalendarIds,
    selectedResourceIds,
    dateRange,
    handleViewTypeChange,
    setViewMode,
    setDateRange,
    handleCalendarSelect,
    handleResourceSelect,
  } = useCalendarViewState(calendarRef, calendars, resources);

  const titleFilter = useCalendarViewStore((s) => s.titleFilter);
  const setTitleFilter = useCalendarViewStore((s) => s.setTitleFilter);

  const [visibleDateRange, setVisibleDateRange] = useState<{
    start: Date;
    end: Date;
  }>();

  // Convert resource IDs to calendar IDs when on Resources tab
  // Backend handles filtering when includeOnlyResources=true
  const calendarIdsForQuery = useMemo(() => {
    if (viewType === CalendarViewType.RESOURCES) {
      // Convert selected resource IDs to their calendar IDs
      const resourceCalendarIds = selectedResourceIds
        .map((resourceId) => {
          const resource = resources?.find((r) => r.id === resourceId);
          return resource?.calendarId;
        })
        .filter((id): id is string => Boolean(id));

      return resourceCalendarIds;
    }
    return selectedCalendarIds;
  }, [viewType, selectedCalendarIds, selectedResourceIds, resources]);

  const {
    currentDate,
    setCurrentDate,
    events,
    isLoading: isLoadingEvents,
    isDeletingEvent,
    primaryCalendar,
    handleDeleteEvent,
    refetchEvents,
  } = useCalendarEvents(
    isNylasConnected,
    calendarIdsForQuery,
    visibleDateRange,
    calendarRef,
    viewType === CalendarViewType.CALENDARS ? 'calendars' : 'resources',
    authedProviderUser?.nylasCalendarProvider || undefined,
  );

  // Event mutation handlers
  const { handleEventDragOrResize } = useEventMutation();

  // Auto refresh
  const refreshCallback = useCallback(() => {
    void refetchEvents();
  }, [refetchEvents]);

  useAutoRefresh(refreshCallback, {
    seconds: 30,
    enabled: isNylasConnected,
  });

  // State for modals and popovers
  const [selectedEvent, setSelectedEvent] =
    useState<NylasCalendarEventDataFragment | null>(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [isEventDetailsPopoverOpen, setIsEventDetailsPopoverOpen] =
    useState(false);
  const [isNewEventModalOpen, setIsNewEventModalOpen] = useState(false);
  const [newEventSelectInfo, setNewEventSelectInfo] = useState(null);
  const [isEditEventModalOpen, setIsEditEventModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false);
  const [targetCalendarId, setTargetCalendarId] = useState<string | null>(null);

  // Update targetCalendarId when primaryCalendar changes
  useEffect(() => {
    setTargetCalendarId(primaryCalendar?.id);
  }, [primaryCalendar]);

  useEffect(() => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      // const currentDate = api.getDate();

      const start = api.view.activeStart;
      const end = api.view.activeEnd;
      logger.debug('load date boundaries:', {
        start,
        end,
      });

      setVisibleDateRange({ start, end });
    }
  }, [viewMode, currentDate]);

  // Memoize plugins array
  const calendarPlugins = useMemo(
    () => [
      luxonPlugin,
      dayGridPlugin,
      timeGridPlugin,
      resourceTimelinePlugin,
      resourceTimeGridPlugin,
      interactionPlugin,
    ],
    [],
  );

  // Memoize views configuration
  const calendarViews = useMemo(
    () => ({
      resourceTimeGridDay: {
        type: 'resourceTimeGrid',
        duration: { days: 3 },
        buttonText: 'Day',
      },
      resourceTimelineMonth: {
        type: 'resourceTimeline',
        duration: { months: 1 },
        buttonText: 'Month',
      },
      timeGridWeek: {
        type: 'timeGrid',
        duration: { weeks: 1 },
        buttonText: 'Week',
      },
      timeGridDay: {
        type: 'timeGrid',
        duration: { days: 1 },
        buttonText: 'Day',
      },
      dayGridMonth: {
        type: 'dayGrid',
        duration: { months: 1 },
        buttonText: 'Month',
      },
    }),
    [],
  );

  // Memoize event handlers
  const handleEventClick = useCallback(({ event, el }) => {
    const original = event.extendedProps.originalEvent;
    if (
      original &&
      typeof original.id === 'string' &&
      typeof original.calendarId === 'string'
    ) {
      setSelectedEvent(original as NylasCalendarEventDataFragment);
      setPopoverAnchorEl(el);
      setIsEventDetailsPopoverOpen(true);
    }
  }, []);

  const handleDeleteClick = useCallback(
    (event: NylasCalendarEventDataFragment) => {
      setSelectedEvent(event);
      setIsConfirmDeleteModalOpen(true);
    },
    [],
  );

  const handleModifyClick = useCallback(
    (event: NylasCalendarEventDataFragment) => {
      setSelectedEvent(event);
      setIsEditEventModalOpen(true);
      setIsEventDetailsPopoverOpen(false);
      setPopoverAnchorEl(null);
    },
    [],
  );

  const delayedRefetch = useCallback(() => {
    logger.debug('🎯 delayedRefetch called');
    setTimeout(() => {
      logger.debug('🎯 Refetching events');
      refetchEvents();
    }, 3000);
  }, [refetchEvents]);

  const handleEventDrop = useCallback(
    (info: EventDropArg) => {
      const calendarId =
        info.event.extendedProps.calendarId || primaryCalendar?.id;
      handleEventDragOrResize({
        info,
        calendarId,
        onSuccess: delayedRefetch,
        onError: info.revert,
      }).catch((error) => {
        // handleEventDragOrResize already reverts the optimistic update and
        // shows a toast on error — log unexpected failures from the error
        // handling path itself (e.g., revert() or delayedRefetch failures)
        logger.error('Calendar drag operation error handling failed', {
          error,
          eventId: info.event.id,
          calendarId,
        });
      });
      return true;
    },
    [primaryCalendar, handleEventDragOrResize, delayedRefetch],
  );

  const handleEventResize = useCallback(
    (info: EventResizeDoneArg) => {
      const calendarId =
        info.event.extendedProps.calendarId || primaryCalendar?.id;
      handleEventDragOrResize({
        info,
        calendarId,
        onSuccess: delayedRefetch,
        onError: info.revert,
      }).catch((error) => {
        // handleEventDragOrResize already reverts the optimistic update and
        // shows a toast on error — log unexpected failures from the error
        // handling path itself (e.g., revert() or delayedRefetch failures)
        logger.error('Calendar drag operation error handling failed', {
          error,
          eventId: info.event.id,
          calendarId,
        });
      });
      return true;
    },
    [primaryCalendar, handleEventDragOrResize, delayedRefetch],
  );

  const filteredResources = useMemo(() => {
    return viewType === CalendarViewType.RESOURCES
      ? resources?.filter((resource) =>
          selectedResourceIds.includes(resource.id),
        )
      : undefined;
  }, [viewType, resources, selectedResourceIds]);

  const handleEventCreated = (newEvent: any) => {
    logger.debug('🎯 handleEventCreated called with:', { newEvent });

    if (calendarRef.current && newEvent?.event) {
      const api = calendarRef.current.getApi();

      // Determine if this is a resource event based on viewType
      const isResourceEvent = viewType === CalendarViewType.RESOURCES;
      logger.debug('🎯 isResourceEvent:', { isResourceEvent });

      // Get the resourceId from the select info if it's a resource event
      // TODO: Pretty hacky selecting the resourceId this way...
      const resourceId = isResourceEvent && newEventSelectInfo?.resource?.id;
      const resource = resourceId
        ? resources?.find((r) => r.id === resourceId)
        : undefined;
      const calendar = getCalendarByEmail(newEvent.event.calendarId);

      const transformedEvent = transformEventForCalendar(newEvent.event, {
        resourceId: isResourceEvent ? resourceId : undefined,
        calendarHexColor:
          resource?.hexColor || calendar?.hexColor || primaryCalendar?.hexColor,
      });

      if (transformedEvent) {
        logger.debug('📅 Adding transformed event to calendar:', {
          transformedEvent,
          isResourceEvent,
          resourceId,
          hexColor:
            resource?.hexColor ||
            calendar?.hexColor ||
            primaryCalendar?.hexColor,
        });
        api.addEvent(transformedEvent);
      }
    }

    // Background refresh after a short delay
    delayedRefetch();
  };

  const handleViewModeChange = (newViewMode: ViewMode) => {
    setViewMode(newViewMode);
    calendarRef.current?.getApi().changeView(newViewMode);
  };

  return (
    <div
      className="flex h-full flex-col"
      onMouseDown={() => {
        if (isEventDetailsPopoverOpen) {
          setIsEventDetailsPopoverOpen(false);
          setPopoverAnchorEl(null);
        }
      }}
      onTouchStart={() => {
        if (isEventDetailsPopoverOpen) {
          setIsEventDetailsPopoverOpen(false);
          setPopoverAnchorEl(null);
        }
      }}
    >
      <CalendarHeader
        viewType={viewType}
        viewMode={viewMode}
        onViewTypeChange={handleViewTypeChange}
        onViewModeChange={handleViewModeChange}
        onTodayClick={() => {
          calendarRef.current?.getApi().today();
          setCurrentDate(DateTime.now());
        }}
        onPrevClick={() => {
          const api = calendarRef.current?.getApi();
          if (api) {
            api.prev();
            setCurrentDate(DateTime.fromJSDate(api.getDate()));
          }
        }}
        onNextClick={() => {
          const api = calendarRef.current?.getApi();
          if (api) {
            api.next();
            setCurrentDate(DateTime.fromJSDate(api.getDate()));
          }
        }}
        isLoading={isLoadingEvents}
        isLoadingCalendars={isLoadingCalendars}
        isLoadingResources={isLoadingResources}
        calendars={calendars}
        resources={resources}
        selectedCalendarIds={selectedCalendarIds}
        selectedResourceIds={selectedResourceIds}
        onCalendarSelect={handleCalendarSelect}
        onResourceSelect={handleResourceSelect}
        dateRange={dateRange}
      />

      <div className="relative flex-1 overflow-auto">
        {/*
         * Only show the empty-state banner when the events query was actually
         * executed. Mirrors the `skip` conditions in useCalendarEvents so we
         * don't say "no events match" when no query ran (e.g. Calendars tab
         * with nothing selected, or before the primary calendar resolves).
         */}
        {titleFilter &&
          isNylasConnected &&
          Boolean(primaryCalendar) &&
          (viewType === CalendarViewType.RESOURCES ||
            calendarIdsForQuery.length > 0) &&
          !isLoadingEvents &&
          events.length === 0 && (
            <div className="pointer-events-none absolute inset-x-0 top-4 z-10 flex justify-center">
              <div className="pointer-events-auto flex items-center gap-3 rounded-md border border-[#FFA41C] bg-[#fffbf0] px-4 py-2 shadow-100">
                <span className="text-small-caption text-neutral-150">
                  No events match{' '}
                  <span className="font-medium">"{titleFilter}"</span>
                </span>
                <button
                  type="button"
                  onClick={() => setTitleFilter(null)}
                  className="text-small-caption font-medium text-green-150 hover:underline focus:outline-none"
                >
                  Clear filter
                </button>
              </div>
            </div>
          )}
        <FullCalendar
          ref={calendarRef}
          initialView={viewMode}
          initialDate={
            dateRange && shouldRestorePersistedDate(viewMode)
              ? resolveCalendarAnchorDate(dateRange).toJSDate()
              : undefined
          }
          plugins={calendarPlugins}
          views={calendarViews}
          /* Room Resources */
          datesAboveResources={true}
          resourceLabelContent={(args) => (
            <span className="text-small-caption font-medium text-neutral-125">
              {args.resource.extendedProps.name}
            </span>
          )}
          resourceLabelClassNames="text-small-caption font-medium text-neutral-125 truncate"
          events={events}
          resources={filteredResources}
          schedulerLicenseKey="0642643305-fcs-1732645980" // TODO: Move this to config
          headerToolbar={false}
          nowIndicator={true}
          allDaySlot={true}
          handleWindowResize={true}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          slotDuration="00:30:00"
          snapDuration="00:15:00"
          firstDay={0} // 0=Sunday, 1=Monday, ...
          selectable={true}
          timeZone="local"
          select={(selectInfo) => {
            logger.debug('🎯 Event Select:', {
              selectInfo,
            });
            setNewEventSelectInfo(selectInfo);
            setIsNewEventModalOpen(true);
          }}
          selectMirror={true}
          unselectAuto={true}
          eventClick={handleEventClick}
          showNonCurrentDates={viewMode === ViewMode.DAY_GRID_MONTH}
          height="100%"
          editable={true}
          eventResizableFromStart={true}
          eventDurationEditable={true}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          dayHeaderContent={(args) => {
            const dayHeaders = [
              'Sunday',
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
            ];
            const dayIndex = args.date.getUTCDay();
            const date = DateTime.fromJSDate(args.date);
            const format = [
              ViewMode.TIME_GRID_DAY,
              ViewMode.TIME_GRID_WEEK,
              ViewMode.RESOURCE_TIME_GRID_DAY,
            ].includes(viewMode)
              ? 'EEEE M/d'
              : 'EEEE';
            return (
              <span className="text-small-caption font-medium text-neutral-125">
                {viewMode === ViewMode.DAY_GRID_MONTH
                  ? dayHeaders[dayIndex]
                  : date.toFormat(format)}
              </span>
            );
          }}
          dayCellContent={(args) => {
            const date = DateTime.fromJSDate(args.date);
            const isToday = date.hasSame(DateTime.now(), 'day');
            const isCurrentMonth = date.hasSame(
              DateTime.fromJSDate(calendarRef.current?.getApi().getDate()),
              'month',
            );

            return (
              <div
                className={`flex h-full flex-col p-1 ${
                  !isCurrentMonth ? 'opacity-50' : ''
                }`}
              >
                <span
                  className={`text-small-caption font-medium ${
                    isToday ? 'text-green-150' : 'text-neutral-125'
                  }`}
                >
                  {date.toFormat('d')}
                </span>
              </div>
            );
          }}
          eventContent={(eventInfo) => {
            if (viewMode === ViewMode.DAY_GRID_MONTH) {
              return <MonthEventContent event={eventInfo.event} />;
            }

            const start = eventInfo.event.start?.getTime() || 0;
            const end = eventInfo.event.end?.getTime() || 0;
            const durationInMinutes = (end - start) / (1000 * 60);
            const isSmallEvent = durationInMinutes <= 30;

            return isSmallEvent ? (
              <SmallEventContent event={eventInfo.event} />
            ) : (
              <RegularEventContent
                event={eventInfo.event}
                timeText={eventInfo.timeText}
              />
            );
          }}
          slotLabelContent={(args) => (
            <span className="text-small-caption font-medium text-neutral-125">
              {args.text}
            </span>
          )}
          allDayContent={() => <></>}
          datesSet={(dateInfo) => {
            const utcStart = DateTime.fromJSDate(dateInfo.start).toUTC();
            const utcEnd = DateTime.fromJSDate(dateInfo.end).toUTC();
            const utcCurrentStart = DateTime.fromJSDate(
              dateInfo.view.currentStart,
            ).toUTC();
            logger.debug('🎯 Dates Set:', {
              dateInfo,
              utcStart: utcStart.toISO(),
              utcEnd: utcEnd.toISO(),
              utcCurrentStart: utcCurrentStart.toISO(),
            });
            setVisibleDateRange({
              start: utcStart.toJSDate(),
              end: utcEnd.toJSDate(),
            });
            setDateRange({
              start: utcStart.toJSDate(),
              end: utcEnd.toJSDate(),
              currentStart: utcCurrentStart.toJSDate(),
            });
          }}
        />
      </div>

      <EventDetailsPopover
        event={selectedEvent}
        referenceElement={popoverAnchorEl}
        isOpen={isEventDetailsPopoverOpen}
        onClose={() => {
          setIsEventDetailsPopoverOpen(false);
          setSelectedEvent(null);
          setPopoverAnchorEl(null);
        }}
        onDeleteEventClick={handleDeleteClick}
        onModifyEventClick={handleModifyClick}
      />

      {isNewEventModalOpen && (
        <CreateEventModal
          key={newEventSelectInfo?.start?.getTime()}
          isOpen={true}
          onClose={() => {
            setIsNewEventModalOpen(false);
            setNewEventSelectInfo(null);
          }}
          selectInfo={newEventSelectInfo}
          calendarId={targetCalendarId}
          onEventCreated={handleEventCreated}
        />
      )}

      {isEditEventModalOpen &&
        selectedEvent &&
        (selectedEvent.calendarId ?? primaryCalendar?.id) && (
          <EditEventModal
            isOpen={true}
            calendarId={selectedEvent.calendarId ?? primaryCalendar?.id ?? ''}
            onClose={() => {
              setIsEditEventModalOpen(false);
              setSelectedEvent(null);
            }}
            event={selectedEvent}
            calendarApi={calendarRef.current?.getApi()}
            onEventUpdated={() => {
              logger.debug('✅ onEventUpdated');
              delayedRefetch();
            }}
          />
        )}

      {isConfirmDeleteModalOpen && (
        <ConfirmDeleteModal
          isOpen={isConfirmDeleteModalOpen}
          setClosed={() => setIsConfirmDeleteModalOpen(false)}
          performDelete={async () => {
            if (selectedEvent) {
              const success = await handleDeleteEvent(selectedEvent);
              if (success) {
                setIsConfirmDeleteModalOpen(false);
                setIsEventDetailsPopoverOpen(false);
                setSelectedEvent(null);
                setPopoverAnchorEl(null);
                delayedRefetch();
              }
            }
          }}
          title="Delete event?"
          fetching={isDeletingEvent}
        >
          Changes will be reflected in your external calendar and any
          participants will be notified.
        </ConfirmDeleteModal>
      )}
    </div>
  );
};

export default Calendar;
